import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useAddCommentMutation,
  useEditCommentMutation,
} from '@app/api/Client/api';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NActionsHistory } from '@app/types';

import type { BaseState } from './useBaseState';

type UseLeadCardAddCommentParams = Pick<
  BaseState,
  | 'leadCardData'
  | 'refetchLeadCard'
  | 'refreshLatestHistory'
  | 'selectedBranchId'
  | 'selectedLeadId'
>;

interface UseLeadCardAddCommentResult {
  commentText: string;
  isCommentEditing: boolean;
  isCommentModalOpen: boolean;
  isCommentSaving: boolean;
  openCommentModal(params?: OpenLeadCardCommentModalParams): void;
  closeCommentModal(): void;
  saveComment(commentText?: string): Promise<boolean>;
  setCommentText(commentText: string): void;
}

export interface OpenLeadCardCommentModalParams {
  commentId?: number;
  actionItems?: NActionsHistory.ModelActionHistoryItem[] | null;
}

export const useAddComment = ({
  leadCardData,
  refetchLeadCard,
  refreshLatestHistory,
  selectedBranchId,
  selectedLeadId,
}: UseLeadCardAddCommentParams): UseLeadCardAddCommentResult => {
  const {
    params: { ActiveBranchId, UserId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [currentCommentId, setCurrentCommentId] = useState<number | null>(null);
  const [addComment, { isLoading: isAddingComment }] = useAddCommentMutation();
  const [editComment, { isLoading: isEditingComment }] =
    useEditCommentMutation();

  const isCommentSaving = isAddingComment || isEditingComment;

  const clientId = useMemo(
    () => leadCardData?.ClientId,
    [leadCardData?.ClientId]
  );

  useEffect(() => {
    setIsCommentModalOpen(false);
    setCommentText('');
    setCurrentCommentId(null);
  }, [selectedBranchId, selectedLeadId]);

  const openCommentModal = useCallback(
    (params: OpenLeadCardCommentModalParams = {}) => {
      if (!clientId) {
        showNotification('Идентификатор клиента не найден', 'frontWarning');
        return;
      }

      const nextCommentText = params.actionItems?.[0]?.ValueNew ?? '';

      setCurrentCommentId(params.commentId ?? null);
      setCommentText(nextCommentText);
      setIsCommentModalOpen(true);
    },
    [clientId, showNotification]
  );

  const closeCommentModal = useCallback(() => {
    if (isCommentSaving) return;

    setIsCommentModalOpen(false);
    setCommentText('');
    setCurrentCommentId(null);
  }, [isCommentSaving]);

  const saveComment = useCallback(
    async (nextCommentText = commentText): Promise<boolean> => {
      if (isCommentSaving) return false;

      const trimmedComment = nextCommentText.trim();

      if (!trimmedComment) return false;

      if (!clientId) {
        showNotification('Идентификатор клиента не найден', 'frontWarning');
        return false;
      }

      if (!UserId) {
        showNotification(
          'Идентификатор пользователя не найден',
          'frontWarning'
        );
        return false;
      }

      if (!ActiveBranchId) {
        showNotification('Филиал не был распознан', 'frontWarning');
        return false;
      }

      try {
        if (currentCommentId !== null) {
          await editComment({
            ActiveBranchId,
            commentId: currentCommentId,
            text: trimmedComment,
          }).unwrap();
        } else {
          await addComment({
            ActiveBranchId,
            data: {
              ClientId: clientId,
              Comment: trimmedComment,
            },
          }).unwrap();
        }

        showNotification(
          currentCommentId !== null
            ? 'Комментарий успешно изменён'
            : 'Комментарий успешно добавлен',
          'success'
        );
        setIsCommentModalOpen(false);
        setCommentText('');
        setCurrentCommentId(null);
        refetchLeadCard();
        void refreshLatestHistory();
        return true;
      } catch (error) {
        showNotification(error);
        return false;
      }
    },
    [
      ActiveBranchId,
      UserId,
      addComment,
      commentText,
      currentCommentId,
      editComment,
      isCommentSaving,
      clientId,
      refetchLeadCard,
      refreshLatestHistory,
      showNotification,
    ]
  );

  return useMemo(
    () => ({
      commentText,
      isCommentEditing: currentCommentId !== null,
      isCommentModalOpen,
      isCommentSaving,
      openCommentModal,
      closeCommentModal,
      saveComment,
      setCommentText,
    }),
    [
      closeCommentModal,
      currentCommentId,
      commentText,
      isCommentModalOpen,
      isCommentSaving,
      openCommentModal,
      saveComment,
    ]
  );
};
